// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Company } from '../../data/companyData'
import {
  getCompanyList,
  setSelectedCompany,
  createNewTag,
  renameTag
} from './thunks'
import { LOGOUT } from '../globalActions'
import { RequestState } from '../common'

export interface CompanyList {
  [id: string]: Company
}

export interface CompanyState {
  /** The currently selected company */
  selectedCompany: Company | null;
  /** The list of all companies */
  companyList: CompanyList;
  /** The state of the get companies request */
  companiesRequestState: RequestState
  renameTagRequestState: RequestState
}

const initialState: CompanyState = {
  selectedCompany: null,
  companyList: {},
  companiesRequestState: 'idle',
  renameTagRequestState: 'idle'
}

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setCompanyRequestState: (
      state,
      action: PayloadAction<'idle' | 'pending' | 'fulfilled' | 'rejected'>
    ) => {
      state.companiesRequestState = action.payload
    },
    setRenameTagRequestState: (
      state,
      action: PayloadAction<'idle' | 'pending' | 'fulfilled' | 'rejected'>
    ) => {
      state.renameTagRequestState = action.payload
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getCompanyList.pending, (state) => {
      state.companiesRequestState = 'pending'
    })

    builder.addCase(getCompanyList.fulfilled, (state, action) => {
      state.companiesRequestState = 'fulfilled'
      state.companyList = action.payload
    })

    builder.addCase(getCompanyList.rejected, (state) => {
      state.companiesRequestState = 'rejected'
    })

    builder.addCase(setSelectedCompany.pending, (state) => {
      state.companiesRequestState = 'pending'
    })

    builder.addCase(setSelectedCompany.fulfilled, (state, action) => {
      state.companiesRequestState = 'fulfilled'
      state.selectedCompany = action.payload
      state.companyList[action.payload.id] = action.payload
    })

    builder.addCase(setSelectedCompany.rejected, (state) => {
      state.companiesRequestState = 'rejected'
    })

    builder.addCase(createNewTag.rejected, () => {
      // TODO
    })

    builder.addCase(createNewTag.pending, () => {
      // TODO
    })

    builder.addCase(createNewTag.fulfilled, (state, action) => {
      const { companyId, tag } = action.payload

      state.selectedCompany?.allTags.push(tag)

      if (state.companyList[companyId] == null) return
      state.companyList[companyId].allTags.push(tag)
    })

    builder.addCase(renameTag.rejected, (state) => {
      state.renameTagRequestState = 'rejected'
    })

    builder.addCase(renameTag.pending, (state) => {
      state.renameTagRequestState = 'pending'
    })

    builder.addCase(renameTag.fulfilled, (state, action) => {
      const { companyId, tagId, newTagLabel } = action.payload

      // Update currently selected company
      if (
        state.selectedCompany
        && state.selectedCompany.id === companyId
      ) {
        state.selectedCompany.allTags.map((tag) => {
          if (tag.id === tagId) {
            tag.label = newTagLabel
          }
          return tag
        })
      }

      // Update the stored company list
      if (state.companyList[companyId] == null) return
      state.companyList[companyId].allTags.forEach((tag) => {
        if (tag.id === tagId) {
          tag.label = newTagLabel
        }
      })

      state.renameTagRequestState = 'fulfilled'
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
  }
})

export default companySlice.reducer
export const {
  setCompanyRequestState,
  setRenameTagRequestState
} = companySlice.actions
